// src/services/plantation.service.ts
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:1920/api';

// ── Types existants ────────────────────────────────────────────────────────────

export interface PlantationParcelle {
  id: string;
  entiteId: string;
  type: 'plantation' | 'secteur' | 'bloc' | 'parcelle';
  parentId?: string;
  code: string;
  nom: string;
  surface?: number;
  latitude?: number;
  longitude?: number;
  typeCulture: 'palmier_huile' | 'hevea' | 'cacao' | 'coton' | 'autre';
  variete?: string;
  annePlantation?: number;
  statut: 'actif' | 'jachère' | 'reconversion';
  children?: PlantationParcelle[];
}

export interface PlantationCampagne {
  id: string;
  entiteId: string;
  code: string;
  libelle: string;
  dateDebut: string;
  dateFin: string;
  statut: 'planifiee' | 'en_cours' | 'cloturee';
  objectifTonnes?: number;
  budgetFCFA?: number;
}

export interface PlantationBonPesee {
  id: string;
  numero: string;
  entiteId?: string;
  campagneId?: string;
  parcelleId?: string;
  journalierId?: string;
  datePesee: string;
  heurePesee?: string;
  produit: 'regime_palmier' | 'latex' | 'feves_cacao' | 'coton_graine' | 'autre';
  poidsBrut: number;
  poidsTare: number;
  poidsNet: number;
  tauxHumidite?: number;
  poidsCorrige?: number;
  source: 'manuel' | 'pont_bascule' | 'mobile';
  ticketExterne?: string;
  statut: 'brouillon' | 'valide' | 'integre';
  parcelle?: PlantationParcelle;
  journalierNom?: string;
}

export interface PlantationBordereau {
  id: string;
  numero: string;
  campagneId?: string;
  periode: string;
  dateDebut: string;
  dateFin: string;
  parcelleId?: string;
  totalPoidsNet: number;
  totalBrut: number;
  tauxUnitaire: number;
  nbBons: number;
  statut: 'brouillon' | 'valide' | 'paye' | 'cloture';
  modePaiement?: string;
  datePaiement?: string;
  parcelle?: PlantationParcelle;
}

export interface RendementStats {
  parcelleId: string;
  parcelle?: PlantationParcelle;
  totalKg: number;
  surfaceHa: number;
  rendementTha: number;
  objectifTha?: number;
  tauxRealisationPct?: number;
}

export interface SyncResult {
  importes: number;
  ignores: number;
}

// ── Nouveaux types SIFCA ───────────────────────────────────────────────────────

export interface PlantationAppelMatinal {
  id: string;
  entiteId: string;
  date: string;
  parcelleId?: string;
  campagneId?: string;
  agentId?: string;
  matricule: string;
  nomAgent?: string;
  typeAgent: 'MO' | 'CEAPI' | 'chauffeur' | 'superviseur' | 'autre';
  present: boolean;
  heureArrivee?: string;
  motifAbsence?: string;
  activitePrevue: 'entretien_sol' | 'elagage' | 'fertilisation' | 'recolte' | 'collecte' | 'supervision' | 'transport' | 'autre';
  parcelle?: PlantationParcelle;
}

export interface StatPresence {
  total: number;
  presents: number;
  absents: number;
  tauxPresence: number;
}

export interface PlantationOperation {
  id: string;
  entiteId?: string;
  date: string;
  typeOperation: string;
  phase: 'planifie' | 'realise';
  statut: string;
  parcelleId?: string;
  campagneId?: string;
  superficiePlanifiee?: number;
  superficieRealisee?: number;
  nombreMO?: number;
  matriculeCEAPI?: string;
  nombrePiedsPlanifies?: number;
  nombrePiedsRealises?: number;
  tonnagePlanifie?: number;
  tonnageRealise?: number;
  nombreSacsPlanifies?: number;
  nombreSacsRealises?: number;
  produitFertilisant?: string;
  nombreRegimesCollectes?: number;
  nombreRegimesBons?: number;
  nombreRegimesVerts?: number;
  nombreRegimesPourris?: number;
  nombreRegimesAnormaux?: number;
  vehiculeImmatriculation?: string;
  chauffeurMatricule?: string;
  observations?: string;
  operationParentId?: string;
  parcelle?: PlantationParcelle;
}

export interface PlantationPointageRegimes {
  id: string;
  entiteId?: string;
  date: string;
  parcelleId?: string;
  campagneId?: string;
  matriculeAAPI?: string;
  matriculeRecolteur?: string;
  nombreTotal: number;
  nombreBons: number;
  nombreVerts: number;
  nombrePourris: number;
  nombreAnormaux: number;
  nombreEgrappes: number;
  statut: 'brouillon' | 'valide';
  parcelle?: PlantationParcelle;
}

export interface PlantationBudget {
  id: string;
  entiteId?: string;
  annee: number;
  mois: number;
  typeActivite: string;
  campagneId?: string;
  budgetAlloue: number;
  realise: number;
  vra: number;
  quantitePlanifiee?: number;
  quantiteRealisee?: number;
  unite?: string;
}

export interface BudgetVsRealise {
  typeActivite: string;
  budgetTotal: number;
  realiseTotal: number;
  vra: number;
  tauxRealisation: number;
}

export interface PlantationVehicule {
  id: string;
  entiteId: string;
  immatriculation: string;
  marque?: string;
  modele?: string;
  type: 'camion' | 'pick-up' | 'tracteur' | 'benne' | 'citerne' | 'autre';
  capaciteChargeKg?: number;
  annee?: number;
  statut: 'actif' | 'en_maintenance' | 'hors_service';
  chauffeurMatricule?: string;
  observations?: string;
}

export interface PlantationTransport {
  id: string;
  entiteId: string;
  date: string;
  vehiculeId?: string;
  immatriculation: string;
  chauffeurMatricule: string;
  typeTransport: 'personnel' | 'outillage' | 'materiel' | 'intrant' | 'recolte' | 'autre';
  activite?: string;
  itinerairePrev?: string;
  itineraireRealise?: string;
  parcelleId?: string;
  campagneId?: string;
  heureDepart?: string;
  heureArrivee?: string;
  kmDebut?: number;
  kmFin?: number;
  kmParcouru?: number;
  heureMoteur?: number;
  vitesseMoyenne?: number;
  chargementKg?: number;
  observations?: string;
  statut: 'planifie' | 'en_cours' | 'termine' | 'annule';
  vehicule?: PlantationVehicule;
}

// ── Types Sûreté SIFCA ────────────────────────────────────────────────────────

export interface SureteIntervention {
  bloc: string;
  actionMenee: string;
  saisies?: string;
  heure?: string;
}

export interface SureteZone {
  bloc: string;
  heure?: string;
  action?: string;
  observations?: string;
}

export interface PlantationSurete {
  id: string;
  entiteId: string;
  date: string;
  immatriculation?: string;
  nomConducteur?: string;
  matriculeAgent?: string;
  heureDepart?: string;
  heureArrivee?: string;
  parcoursZones: SureteZone[];
  interventions: SureteIntervention[];
  anomaliesDetectees?: string;
  rapport?: string;
  statut: 'planifie' | 'en_cours' | 'termine';
  vra?: number;
  campagneId?: string;
}

// ── Service ───────────────────────────────────────────────────────────────────

class PlantationService {
  private getHeaders() {
    const token = localStorage.getItem('token') || localStorage.getItem('access_token');
    return { Authorization: `Bearer ${token}` };
  }

  // Parcellaire
  async listParcelles(entiteId: string): Promise<PlantationParcelle[]> {
    const r = await axios.get(`${API_URL}/plantation/parcelles`, {
      headers: this.getHeaders(),
      params: { entiteId },
    });
    return r.data;
  }

  async getArbre(entiteId: string): Promise<PlantationParcelle[]> {
    const r = await axios.get(`${API_URL}/plantation/parcelles/arbre`, {
      headers: this.getHeaders(),
      params: { entiteId },
    });
    return r.data;
  }

  async createParcelle(data: Partial<PlantationParcelle>): Promise<PlantationParcelle> {
    const r = await axios.post(`${API_URL}/plantation/parcelles`, data, {
      headers: this.getHeaders(),
    });
    return r.data;
  }

  async updateParcelle(id: string, data: Partial<PlantationParcelle>): Promise<PlantationParcelle> {
    const r = await axios.put(`${API_URL}/plantation/parcelles/${id}`, data, {
      headers: this.getHeaders(),
    });
    return r.data;
  }

  async deleteParcelle(id: string): Promise<void> {
    await axios.delete(`${API_URL}/plantation/parcelles/${id}`, {
      headers: this.getHeaders(),
    });
  }

  // Campagnes
  async listCampagnes(entiteId: string): Promise<PlantationCampagne[]> {
    const r = await axios.get(`${API_URL}/plantation/campagnes`, {
      headers: this.getHeaders(),
      params: { entiteId },
    });
    return r.data;
  }

  async createCampagne(data: Partial<PlantationCampagne>): Promise<PlantationCampagne> {
    const r = await axios.post(`${API_URL}/plantation/campagnes`, data, {
      headers: this.getHeaders(),
    });
    return r.data;
  }

  async updateCampagne(id: string, data: Partial<PlantationCampagne>): Promise<PlantationCampagne> {
    const r = await axios.put(`${API_URL}/plantation/campagnes/${id}`, data, {
      headers: this.getHeaders(),
    });
    return r.data;
  }

  // Bons de pesée
  async listBons(params: {
    entiteId: string;
    campagneId?: string;
    parcelleId?: string;
    statut?: string;
    dateDebut?: string;
    dateFin?: string;
  }): Promise<PlantationBonPesee[]> {
    const r = await axios.get(`${API_URL}/plantation/bons-pesee`, {
      headers: this.getHeaders(),
      params,
    });
    return r.data;
  }

  async createBon(data: Partial<PlantationBonPesee>): Promise<PlantationBonPesee> {
    const r = await axios.post(`${API_URL}/plantation/bons-pesee`, data, {
      headers: this.getHeaders(),
    });
    return r.data;
  }

  async validerBon(id: string): Promise<PlantationBonPesee> {
    const r = await axios.post(`${API_URL}/plantation/bons-pesee/${id}/valider`, {}, {
      headers: this.getHeaders(),
    });
    return r.data;
  }

  async syncPontBascule(entiteId: string, campagneId?: string): Promise<SyncResult> {
    const r = await axios.post(`${API_URL}/plantation/bons-pesee/sync-pont-bascule`, {
      entiteId,
      campagneId,
    }, { headers: this.getHeaders() });
    return r.data;
  }

  // Bordereaux
  async listBordereaux(params: {
    entiteId: string;
    campagneId?: string;
    statut?: string;
  }): Promise<PlantationBordereau[]> {
    const r = await axios.get(`${API_URL}/plantation/bordereaux`, {
      headers: this.getHeaders(),
      params,
    });
    return r.data;
  }

  async createBordereau(data: {
    entiteId: string;
    campagneId?: string;
    periode: string;
    parcelleId?: string;
    tauxUnitaire: number;
  }): Promise<PlantationBordereau> {
    const r = await axios.post(`${API_URL}/plantation/bordereaux`, data, {
      headers: this.getHeaders(),
    });
    return r.data;
  }

  async payerBordereau(id: string, data: {
    modePaiement: string;
    datePaiement: string;
    reference?: string;
    signataire?: string;
    caisseId?: string;
  }): Promise<PlantationBordereau> {
    const r = await axios.post(`${API_URL}/plantation/bordereaux/${id}/payer`, data, {
      headers: this.getHeaders(),
    });
    return r.data;
  }

  // Rendements
  async getRendements(params: {
    entiteId: string;
    campagneId?: string;
    parcelleId?: string;
    groupBy?: 'parcelle' | 'bloc' | 'secteur';
  }): Promise<RendementStats[]> {
    const r = await axios.get(`${API_URL}/plantation/rendements`, {
      headers: this.getHeaders(),
      params,
    });
    return r.data;
  }

  // ── Appel matinal ──────────────────────────────────────────────────────────

  async createAppelMatinal(data: Partial<PlantationAppelMatinal>): Promise<PlantationAppelMatinal> {
    const r = await axios.post(`${API_URL}/plantation/appel-matinal`, data, {
      headers: this.getHeaders(),
    });
    return r.data;
  }

  async listAppelMatinal(
    entiteId: string,
    filters: { date?: string; parcelleId?: string },
  ): Promise<PlantationAppelMatinal[]> {
    const r = await axios.get(`${API_URL}/plantation/appel-matinal`, {
      headers: this.getHeaders(),
      params: { entiteId, ...filters },
    });
    return r.data;
  }

  async getStatPresence(entiteId: string, date: string): Promise<StatPresence> {
    const r = await axios.get(`${API_URL}/plantation/appel-matinal/stats`, {
      headers: this.getHeaders(),
      params: { entiteId, date },
    });
    return r.data;
  }

  // ── Opérations ─────────────────────────────────────────────────────────────

  async createOperation(data: Partial<PlantationOperation>): Promise<PlantationOperation> {
    const r = await axios.post(`${API_URL}/plantation/operations`, data, {
      headers: this.getHeaders(),
    });
    return r.data;
  }

  async listOperations(
    entiteId: string,
    filters: { date?: string; typeOperation?: string; phase?: string; campagneId?: string },
  ): Promise<PlantationOperation[]> {
    const r = await axios.get(`${API_URL}/plantation/operations`, {
      headers: this.getHeaders(),
      params: { entiteId, ...filters },
    });
    return r.data;
  }

  async validerOperation(id: string): Promise<PlantationOperation> {
    const r = await axios.post(`${API_URL}/plantation/operations/${id}/valider`, {}, {
      headers: this.getHeaders(),
    });
    return r.data;
  }

  async realiserOperation(planifieId: string, data: Partial<PlantationOperation>): Promise<PlantationOperation> {
    const r = await axios.post(`${API_URL}/plantation/operations/${planifieId}/realiser`, data, {
      headers: this.getHeaders(),
    });
    return r.data;
  }

  // ── Pointage régimes ───────────────────────────────────────────────────────

  async createPointageRegimes(data: Partial<PlantationPointageRegimes>): Promise<PlantationPointageRegimes> {
    const r = await axios.post(`${API_URL}/plantation/pointage-regimes`, data, {
      headers: this.getHeaders(),
    });
    return r.data;
  }

  async listPointageRegimes(
    entiteId: string,
    filters: { date?: string; parcelleId?: string; campagneId?: string },
  ): Promise<PlantationPointageRegimes[]> {
    const r = await axios.get(`${API_URL}/plantation/pointage-regimes`, {
      headers: this.getHeaders(),
      params: { entiteId, ...filters },
    });
    return r.data;
  }

  async validerPointage(id: string): Promise<PlantationPointageRegimes> {
    const r = await axios.post(`${API_URL}/plantation/pointage-regimes/${id}/valider`, {}, {
      headers: this.getHeaders(),
    });
    return r.data;
  }

  // ── Budget ─────────────────────────────────────────────────────────────────

  async upsertBudget(entiteId: string, data: Partial<PlantationBudget>): Promise<PlantationBudget> {
    const r = await axios.post(`${API_URL}/plantation/budget`, { entiteId, ...data }, {
      headers: this.getHeaders(),
    });
    return r.data;
  }

  async getBudgetVsRealise(
    entiteId: string,
    filters: { campagneId?: string; annee?: number },
  ): Promise<BudgetVsRealise[]> {
    const r = await axios.get(`${API_URL}/plantation/budget/vs-realise`, {
      headers: this.getHeaders(),
      params: { entiteId, ...filters },
    });
    return r.data;
  }

  // ── Véhicules ──────────────────────────────────────────────────────────────

  async listVehicules(entiteId: string): Promise<PlantationVehicule[]> {
    const r = await axios.get(`${API_URL}/plantation/vehicules`, {
      headers: this.getHeaders(), params: { entiteId },
    });
    return r.data;
  }

  async createVehicule(data: Partial<PlantationVehicule>): Promise<PlantationVehicule> {
    const r = await axios.post(`${API_URL}/plantation/vehicules`, data, {
      headers: this.getHeaders(),
    });
    return r.data;
  }

  async updateVehicule(id: string, data: Partial<PlantationVehicule>): Promise<PlantationVehicule> {
    const r = await axios.put(`${API_URL}/plantation/vehicules/${id}`, data, {
      headers: this.getHeaders(),
    });
    return r.data;
  }

  async deleteVehicule(id: string): Promise<void> {
    await axios.delete(`${API_URL}/plantation/vehicules/${id}`, {
      headers: this.getHeaders(),
    });
  }

  // ── Transports ─────────────────────────────────────────────────────────────

  async listTransports(entiteId: string, filters: { date?: string; campagneId?: string; typeTransport?: string } = {}): Promise<PlantationTransport[]> {
    const r = await axios.get(`${API_URL}/plantation/transports`, {
      headers: this.getHeaders(), params: { entiteId, ...filters },
    });
    return r.data;
  }

  async createTransport(data: Partial<PlantationTransport>): Promise<PlantationTransport> {
    const r = await axios.post(`${API_URL}/plantation/transports`, data, { headers: this.getHeaders() });
    return r.data;
  }

  async updateTransport(id: string, data: Partial<PlantationTransport>): Promise<PlantationTransport> {
    const r = await axios.put(`${API_URL}/plantation/transports/${id}`, data, { headers: this.getHeaders() });
    return r.data;
  }

  async deleteTransport(id: string): Promise<void> {
    await axios.delete(`${API_URL}/plantation/transports/${id}`, { headers: this.getHeaders() });
  }

  // ── Sûreté ─────────────────────────────────────────────────────────────────

  async listSuretes(entiteId: string, filters: { date?: string; campagneId?: string } = {}): Promise<PlantationSurete[]> {
    const r = await axios.get(`${API_URL}/plantation/suretes`, {
      headers: this.getHeaders(), params: { entiteId, ...filters },
    });
    return r.data;
  }

  async createSurete(data: Partial<PlantationSurete>): Promise<PlantationSurete> {
    const r = await axios.post(`${API_URL}/plantation/suretes`, data, { headers: this.getHeaders() });
    return r.data;
  }

  async updateSurete(id: string, data: Partial<PlantationSurete>): Promise<PlantationSurete> {
    const r = await axios.put(`${API_URL}/plantation/suretes/${id}`, data, { headers: this.getHeaders() });
    return r.data;
  }

  async deleteSurete(id: string): Promise<void> {
    await axios.delete(`${API_URL}/plantation/suretes/${id}`, { headers: this.getHeaders() });
  }
}

export const plantationService = new PlantationService();
